/** AP-37 Detailblatt O — Cockpit „Materialanfragen" (`/calculation-requests`). */
import { api } from '@/lib/axios'
import { downloadFile } from '@/lib/download'
import type { ExportRequest } from '@/lib/export-request'
import type {
  ApplyResult,
  ComparisonResult,
  CreateRequestsParams,
  CreateRequestsResult,
  MaterialListParams,
  MaterialListResponse,
  RequestDetail,
  RequestRow,
} from '../types/calculation-requests.types'

export const fetchMaterial = (params: MaterialListParams) =>
  api.get<MaterialListResponse>('/calculation-requests/material', { params }).then((r) => r.data)

export const materialExportRequest = (
  params: MaterialListParams & { format: string; orientation?: string },
  columns: { key: string; label: string }[],
): ExportRequest => ({
  url: '/calculation-requests/material/export',
  params: { ...params, columns: JSON.stringify(columns) },
})

export const materialUeberspringen = (costLineIds: string[], skipped: boolean) =>
  api.post<{ data: { updated: number } }>('/calculation-requests/material/skip', { costLineIds, skipped }).then((r) => r.data.data)

export const materialKopieren = (costLineIds: string[]) =>
  api.post<{ data: { copied: number } }>('/calculation-requests/material/copy', { costLineIds }).then((r) => r.data.data)

export const preisanfragenErzeugen = (params: CreateRequestsParams) =>
  api.post<{ data: CreateRequestsResult }>('/calculation-requests', params).then((r) => r.data.data)

export const lieferantenSuchen = (q?: string) =>
  api.get<{ data: { id: string; name: string; clientNumber: string }[] }>('/calculations/lookups/suppliers', { params: { q } }).then((r) => r.data.data)

// ── O4 ──────────────────────────────────────────────────────────────────────

export interface ListRequestsParams {
  status?: string
  supplierId?: string
  calculationId?: string
  overdue?: boolean
  kind?: 'MATERIAL' | 'SERVICES'
  search?: string
  sortBy?: string
  sortDir?: 'asc' | 'desc'
}

export const fetchRequests = (params: ListRequestsParams) =>
  api.get<{ data: RequestRow[]; meta: { total: number } }>('/calculation-requests', { params }).then((r) => r.data)

export const fetchRequest = (id: string) => api.get<{ data: RequestDetail }>(`/calculation-requests/${id}`).then((r) => r.data.data)

export const requestUrl = (id: string) => `/calculation-requests/${id}`
export const requestLineUrl = (id: string, lid: string) => `/calculation-requests/${id}/lines/${lid}`

export const closeRequest = (id: string) => api.post<{ data: RequestDetail }>(`/calculation-requests/${id}/close`).then((r) => r.data.data)
export const reopenRequest = (id: string) => api.post<{ data: RequestDetail }>(`/calculation-requests/${id}/reopen`).then((r) => r.data.data)

export const uploadRequestFile = (id: string, datei: File) => {
  const form = new FormData()
  form.append('file', datei)
  return api.post<{ data: RequestDetail }>(`/calculation-requests/${id}/files`, form).then((r) => r.data.data)
}
export const deleteRequestFile = (id: string, fid: string) => api.delete<{ data: RequestDetail }>(`/calculation-requests/${id}/files/${fid}`).then((r) => r.data.data)

export async function oeffneAngebotsdatei(id: string, fid: string, name: string): Promise<void> {
  const res = await api.get<ArrayBuffer>(`/calculation-requests/${id}/files/${fid}/content`, { responseType: 'arraybuffer' })
  const mime = String(res.headers['content-type'] ?? 'application/octet-stream').split(';')[0]
  downloadFile(res.data, name, mime)
}

export const fetchComparison = (params: { calculationId?: string; supplierId?: string; open?: boolean }) =>
  api.get<{ data: ComparisonResult }>('/calculation-requests/comparison', { params }).then((r) => r.data.data)

/**
 * AP-39 Nachtrag 2 (6.2) — der Preisspiegel **einer** Kalkulation für das Fenster „Angebote übernehmen": braucht
 * nur ihr Leserecht und `calculation.cost.view`, nicht das Recht des Einkaufs (PE-119).
 */
export const fetchCalculationComparison = (calculationId: string, params: { supplierId?: string; open?: boolean }) =>
  api.get<{ data: ComparisonResult }>(`/calculation-requests/comparison/calculation/${calculationId}`, { params }).then((r) => r.data.data)

export const applyPrices = (body: { choices: { costLineId: string; requestLineId: string }[]; updateArticles?: boolean; stands?: { calculationId: string; updatedAt: string }[]; dryRun?: boolean }) =>
  api.post<{ data: ApplyResult }>('/calculation-requests/apply', body).then((r) => r.data.data)

// ── Detailblatt K (K-NU) — Anfragen an Nachunternehmer ──────────────────────

export interface LeistungsAnfrage {
  calculationId: string
  positionIds: string[]
  supplierIds: string[]
  attachmentFormat?: 'ONLV' | 'XLSX' | 'BOTH'
  withLongTexts?: boolean
  dueAt?: string
  dryRun?: boolean
}
export interface LeistungsAnfrageErgebnis {
  dryRun: boolean
  documents: { supplierId: string; supplierName: string; lines: number; attachments: string[]; dueAt: string }[]
  created: { requestId: string; documentId: string; documentNumber: string | null; supplierName: string; lines: number; skippedFiles: { fileName: string; reason: string }[] }[]
  failed: { supplierName: string; message: string }[]
}
export const leistungenAnfragen = (body: LeistungsAnfrage) =>
  api.post<{ data: LeistungsAnfrageErgebnis }>('/calculation-requests/services', body).then((r) => r.data.data)

export interface LeistungsAngebot {
  requestLineId: string
  requestId: string
  documentNumber: string | null
  supplierId: string
  supplierName: string
  status: string
  unitPrice: number | null
  net: number | null
  notOffered: boolean
  note: string | null
  applied: boolean
  best: boolean
}
export interface LeistungsZeile {
  positionId: string
  label: string
  shortText: string
  quantity: number
  unitCode: string | null
  offers: LeistungsAngebot[]
}
export const leistungenPreisspiegel = (calculationId: string) =>
  api.get<{ data: LeistungsZeile[] }>('/calculation-requests/services/comparison', { params: { calculationId } }).then((r) => r.data.data)
export const leistungenUebernehmen = (body: { calculationId: string; choices: { positionId: string; requestLineId: string }[]; dryRun?: boolean }) =>
  api.post<{ data: { dryRun: boolean; priceBefore: number; priceAfter: number } }>('/calculation-requests/services/apply', body).then((r) => r.data.data)
export const antwortLesen = (rid: string, fid: string, dryRun: boolean) =>
  api
    .post<{ data: { dryRun: boolean; lines: { lineId: string; name: string; before: number | null; after: number | null; notOffered: boolean; note: string | null; changed: boolean }[] } }>(`/calculation-requests/${rid}/files/${fid}/read`, { dryRun })
    .then((r) => r.data.data)
export async function ladeTeilLv(calculationId: string, positionIds: string[], format: 'ONLV' | 'XLSX', name: string): Promise<void> {
  const res = await api.get<ArrayBuffer>(`/calculations/${calculationId}/sub-part`, { params: { positionIds: positionIds.join(','), format }, responseType: 'arraybuffer' })
  downloadFile(res.data, name, String(res.headers['content-type'] ?? 'application/octet-stream').split(';')[0])
}

// Detailblatt N 6.1a (N1) — Angebot zu einer Anfrage mit der KI lesen; Vorschläge bestätigen oder verwerfen.
export const anfrageKiLesen = (rid: string, fid: string) =>
  api.post<{ data: RequestDetail }>(`/calculation-requests/${rid}/files/${fid}/ai-read`).then((r) => r.data.data)
export const anfrageKiBestaetigen = (rid: string, lineIds: string[], head: boolean) =>
  api.post<{ data: RequestDetail }>(`/calculation-requests/${rid}/ai-suggestions/confirm`, { lineIds, head }).then((r) => r.data.data)
export const anfrageKiVerwerfen = (rid: string, lineIds: string[], head: boolean) =>
  api.post<{ data: RequestDetail }>(`/calculation-requests/${rid}/ai-suggestions/reject`, { lineIds, head }).then((r) => r.data.data)
